import { useNavigate, Link } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '../lib/auth'
import { LogOut, Settings, Menu, X } from 'lucide-react'

export default function Header() {
  const navigate = useNavigate()
  const { user, profile, logout } = useAuthStore()
  const getSubscriptionInfo = useAuthStore((s) => s.getSubscriptionInfo)
  const getCreditsInfo = useAuthStore((s) => s.getCreditsInfo)
  const subInfo = user ? getSubscriptionInfo() : null
  const credits = user ? getCreditsInfo() : null
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const profileRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [profileRef])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false)
  }, [navigate])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (err) {
      console.error('Logout error:', err)
    }
  }

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-md bg-[#0a0e17]/80 border-b border-white/5">
      <nav className="p-4 flex justify-between items-center w-full max-w-7xl mx-auto">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <svg className="w-8 h-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span
            className="text-2xl font-bold text-white tracking-wide group-hover:opacity-90 transition-opacity"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            ScamView
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              {subInfo && subInfo.isTrial && subInfo.plan === 'basic' ? (
                <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full border border-purple-500/30">
                  Basic + Premium Trial ({subInfo.daysRemaining}d)
                </span>
              ) : subInfo && subInfo.isTrial ? (
                <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded-full border border-cyan-500/30">
                  Trial: {subInfo.daysRemaining}d left
                </span>
              ) : null}
              {subInfo && subInfo.plan === 'premium' && !subInfo.isTrial && (
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full border border-amber-500/30">
                  Premium
                </span>
              )}
              {subInfo && subInfo.plan === 'basic' && !subInfo.isTrial && !subInfo.isPremium && (
                <span className="text-xs bg-white/10 text-gray-400 px-2 py-1 rounded-full border border-white/10">
                  Basic
                </span>
              )}
              {subInfo && subInfo.isCancelled && (
                <span className="text-xs bg-red-500/10 text-red-400 px-2 py-1 rounded-full border border-red-500/30">
                  Cancelled
                </span>
              )}
              {credits && (
                <Link
                  to="/dashboard"
                  title={`${credits.monthly} monthly + ${credits.purchased} purchased`}
                  className="text-xs bg-emerald-500/15 text-emerald-400 px-2.5 py-1 rounded-full border border-emerald-500/30 hover:bg-emerald-500/25 transition"
                >
                  {credits.total} credits
                </Link>
              )}
              {profile?.is_admin && (
                <Link
                  to="/admin"
                  className="text-xs bg-red-500/15 text-red-400 px-2.5 py-1 rounded-full border border-red-500/30 hover:bg-red-500/25 transition"
                >
                  Admin
                </Link>
              )}
              <Link to="/dashboard" className="btn-secondary text-sm">Dashboard</Link>
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-1.5 text-gray-400 hover:text-white transition text-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {profile?.username || profile?.first_name || 'Account'}
                  <svg
                    className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#0a0e17] border border-white/10 rounded-xl shadow-2xl py-2 animate-in fade-in zoom-in-95 duration-200 z-50">
                    <button
                      onClick={() => { navigate('/settings'); setIsProfileOpen(false) }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" /> Settings
                    </button>
                    <button
                      onClick={() => { handleLogout(); setIsProfileOpen(false) }}
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-white/5 hover:text-red-300 transition flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/how-to" className="text-sm text-gray-400 hover:text-white transition">How It Works</Link>
              <Link to="/faq" className="text-sm text-gray-400 hover:text-white transition">FAQ</Link>
              <Link to="/plans" className="text-sm text-gray-400 hover:text-white transition">Pricing</Link>
              <Link to="/login" className="btn-secondary text-sm">Login</Link>
              <Link to="/plans" className="btn-primary text-sm">Get Started</Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-gray-400 hover:text-white transition p-1"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          aria-label="Toggle menu"
        >
          {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile drawer */}
      {isMobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#0a0e17]/95 backdrop-blur-md px-4 py-4 flex flex-col gap-3">
          {user ? (
            <>
              {/* Plan + credits row */}
              <div className="flex flex-wrap gap-2 pb-2 border-b border-white/10">
                {subInfo && subInfo.plan === 'premium' && !subInfo.isTrial && (
                  <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full border border-amber-500/30">Premium</span>
                )}
                {subInfo && subInfo.plan === 'basic' && !subInfo.isTrial && (
                  <span className="text-xs bg-white/10 text-gray-400 px-2 py-1 rounded-full border border-white/10">Basic</span>
                )}
                {subInfo && subInfo.isTrial && (
                  <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded-full border border-cyan-500/30">
                    Trial: {subInfo.daysRemaining}d left
                  </span>
                )}
                {credits && (
                  <span className="text-xs bg-emerald-500/15 text-emerald-400 px-2.5 py-1 rounded-full border border-emerald-500/30">
                    {credits.total} credits
                  </span>
                )}
              </div>
              <Link to="/dashboard" onClick={() => setIsMobileOpen(false)} className="text-white font-medium py-2 hover:text-cyan-400 transition">Dashboard</Link>
              <Link to="/settings" onClick={() => setIsMobileOpen(false)} className="text-gray-300 py-2 hover:text-white transition flex items-center gap-2"><Settings className="w-4 h-4" /> Settings</Link>
              {profile?.is_admin && (
                <Link to="/admin" onClick={() => setIsMobileOpen(false)} className="text-red-400 py-2 hover:text-red-300 transition">Admin</Link>
              )}
              <button
                onClick={() => { handleLogout(); setIsMobileOpen(false) }}
                className="text-left text-red-400 py-2 hover:text-red-300 transition flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/how-to" onClick={() => setIsMobileOpen(false)} className="text-gray-300 py-2 hover:text-white transition">How It Works</Link>
              <Link to="/faq" onClick={() => setIsMobileOpen(false)} className="text-gray-300 py-2 hover:text-white transition">FAQ</Link>
              <Link to="/plans" onClick={() => setIsMobileOpen(false)} className="text-gray-300 py-2 hover:text-white transition">Pricing</Link>
              <div className="flex gap-3 pt-2 border-t border-white/10">
                <Link to="/login" onClick={() => setIsMobileOpen(false)} className="btn-secondary flex-1 text-center text-sm">Login</Link>
                <Link to="/plans" onClick={() => setIsMobileOpen(false)} className="btn-primary flex-1 text-center text-sm">Get Started</Link>
              </div>
            </>
          )}
        </div>
      )}
    </header>
  )
}
